import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'framer-motion'
import { Button } from './ui/button'
import { Skeleton } from './ui/skeleton'
import { featuredLocations } from '../data/popular-locations'
import { generateLocationUrl } from '../utils/seoUrls'
import { supabase } from '../lib/supabase'
import { getPopularCities, type City } from '../services/citiesService'
import { generateCitySlug } from '../services/dynamicLocationsService'

interface PopularDestinationsProps {
  showLoadingDemo?: boolean;
}

interface LocationWithPrice {
  id: string;
  slug: string;
  name: string;
  latitude: number;
  longitude: number;
  imageUrl: string;
  featured: boolean;
  startingPrice: number;
  actualPrice?: number;
}

export function PopularDestinations({ showLoadingDemo = false }: PopularDestinationsProps) {
  const navigate = useNavigate()
  const { lang } = useParams<{ lang: string }>()
  const { t } = useTranslation()
  const shouldReduceMotion = useReducedMotion()
  const [loading, setLoading] = useState(showLoadingDemo)
  const [locationsWithPrices, setLocationsWithPrices] = useState<LocationWithPrice[]>(featuredLocations)
  const [pricesLoading, setPricesLoading] = useState(false)
  const [databaseCities, setDatabaseCities] = useState<City[]>([])
  const [citiesLoading, setCitiesLoading] = useState(false)
  const [useDatabaseCities, setUseDatabaseCities] = useState(false)

  // Fetch popular cities from database
  const fetchDatabaseCities = async () => {
    setCitiesLoading(true);
    try {
      const cities = await getPopularCities('Morocco', 5);
      setDatabaseCities(cities);
      setUseDatabaseCities(cities.length > 0);
    } catch (error) {
      console.error('Error fetching database cities:', error);
      setDatabaseCities([]);
      setUseDatabaseCities(false);
    } finally {
      setCitiesLoading(false);
    }
  };

  // Fetch actual lowest prices for database cities
  const fetchDatabaseCitiesPrices = async () => {
    if (databaseCities.length === 0) return;
    
    setPricesLoading(true);
    try {
      const updatedCities = await Promise.all(
        databaseCities.map(async (city) => {
          try {
            // Query cars in this city and get the minimum price
            const { data: cars, error } = await supabase
              .from('cars')
              .select('price_per_day, agencies!inner(city)')
              .eq('agencies.city', city.name)
              .eq('availability', true)
              .order('price_per_day', { ascending: true })
              .limit(1);

            if (error) {
              console.warn(`Error fetching prices for ${city.name}:`, error);
              return { ...city, actualPrice: undefined };
            }

            const actualPrice = cars && cars.length > 0 ? cars[0].price_per_day : null;
            
            return {
              ...city,
              actualPrice: actualPrice ? Number(actualPrice) : undefined
            };
          } catch (err) {
            console.warn(`Error processing ${city.name}:`, err);
            return { ...city, actualPrice: undefined };
          }
        })
      );
      
      setDatabaseCities(updatedCities);
    } catch (error) {
      console.error('Error fetching database cities prices:', error);
    } finally {
      setPricesLoading(false);
    }
  };

  // Fetch actual lowest prices from database (for static locations)
  const fetchActualPrices = async () => {
    setPricesLoading(true);
    try {
      const updatedLocations = await Promise.all(
        featuredLocations.map(async (location) => {
          try {
            // Extract city name from location name (e.g., "Casablanca, Morocco" -> "Casablanca")
            const cityName = location.name.split(',')[0].trim();
            
            // Query cars in this city and get the minimum price
            const { data: cars, error } = await supabase
              .from('cars')
              .select('price_per_day, agencies!inner(city)')
              .eq('agencies.city', cityName)
              .eq('availability', true)
              .order('price_per_day', { ascending: true })
              .limit(1);

            if (error) {
              console.warn(`Error fetching prices for ${cityName}:`, error);
              return location;
            }

            const actualPrice = cars && cars.length > 0 ? cars[0].price_per_day : null;
            
            return {
              ...location,
              actualPrice: actualPrice ? Number(actualPrice) : undefined
            };
          } catch (err) {
            console.warn(`Error processing ${location.name}:`, err);
            return location;
          }
        })
      );
      
      setLocationsWithPrices(updatedLocations);
    } catch (error) {
      console.error('Error fetching actual prices:', error);
    } finally {
      setPricesLoading(false);
    }
  };

  // Simulate loading for demonstration purposes (only if showLoadingDemo is true)
  useEffect(() => {
    if (showLoadingDemo) {
      const timer = setTimeout(() => {
        setLoading(false)
      }, 1500) // Simulate 1.5s loading time

      return () => clearTimeout(timer)
    }
  }, [showLoadingDemo])

  // Fetch database cities on component mount
  useEffect(() => {
    fetchDatabaseCities();
  }, []);

  // Fetch prices for database cities when they're loaded
  useEffect(() => {
    if (databaseCities.length > 0) {
      fetchDatabaseCitiesPrices();
    }
  }, [databaseCities.length]);

  // Fetch actual prices for static locations (fallback)
  useEffect(() => {
    if (!useDatabaseCities) {
      fetchActualPrices();
    }
  }, [useDatabaseCities])

  // Professional animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: shouldReduceMotion ? 0 : 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: shouldReduceMotion ? 0 : 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
  }

  const handleLocationClick = (location: any) => {
    // Generate SEO-friendly URL using the location slug
    const currentLang = (lang || 'fr') as 'fr' | 'en' | 'ar'
    const locationUrl = generateLocationUrl(location.slug, currentLang)
    
    // Navigate to clean SEO-friendly URL without query parameters
    navigate(`/${currentLang}${locationUrl}`)
  }

  return (
    <section className="py-12 md:py-16 bg-gradient-to-b from-background to-background relative overflow-hidden">
      {/* Map Background - Left Bottom - Light Mode */}
      <div 
        className="absolute left-0 bottom-0 bg-no-repeat opacity-10 dark:hidden"
        style={{
          backgroundImage: 'url(/images/mapLight.png)',
          backgroundPosition: 'left bottom',
          backgroundSize: 'contain',
          filter: 'grayscale(100%)',
          width: '400px',
          height: '500px'
        }}
      />
      
      {/* Map Background - Left Bottom - Dark Mode */}
      <div 
        className="absolute left-0 bottom-0 bg-no-repeat opacity-20 hidden dark:block"
        style={{
          backgroundImage: 'url(/images/mapDark.png)',
          backgroundPosition: 'left bottom',
          backgroundSize: 'contain',
          filter: 'grayscale(100%)',
          width: '400px',
          height: '500px'
        }}
      />
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: shouldReduceMotion ? 0 : 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          viewport={{ once: true, margin: "-50px" }}
          className="mb-8 md:mb-12"
        >
          <div className="text-left">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-poppins font-black text-foreground mb-2">
              {t('popularDestinations', 'Destinations Populaires')}
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl">
              {t('popularDestinationsDesc', 'Découvrez nos destinations les plus demandées pour votre prochaine location')}
            </p>
          </div>
        </motion.div>

        <div className="flex gap-3 md:gap-4 overflow-x-auto pb-4 pr-4 sm:grid sm:grid-cols-3 lg:grid-cols-5 sm:overflow-x-visible sm:pb-0 sm:pr-0 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
          {loading ? (
            // Simple professional loading skeleton
            Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="relative group flex-shrink-0 w-40 sm:w-auto">
                <div className="aspect-square overflow-hidden rounded-xl bg-gray-200 dark:bg-muted">
                </div>
                
                <div className="pt-3 sm:pt-4 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))
          ) : useDatabaseCities && databaseCities.length > 0 ? (
            // Use database cities with images
            databaseCities.slice(0, 5).map((city, index) => (
              <motion.article
                key={city.id}
                initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: shouldReduceMotion ? 0 : 0.5, 
                  delay: shouldReduceMotion ? 0 : index * 0.05,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                viewport={{ once: true }}
                className="relative group cursor-pointer flex-shrink-0 w-40 sm:w-auto"
                onClick={() => {
                  // Use proper slug generation for city names
                  const citySlug = generateCitySlug(city.name)
                  const locationUrl = generateLocationUrl(citySlug, (lang as 'fr' | 'en' | 'ar') || 'fr')
                  navigate(`/${lang || 'fr'}${locationUrl}`)
                }}
              >
                <div className="aspect-square overflow-hidden rounded-xl bg-muted">
                  {city.image_url ? (
                    <img
                      src={city.image_url}
                      alt={city.name}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        // Fallback to gradient background if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.className += ' bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 flex items-center justify-center';
                          parent.innerHTML = '<div class="text-center"><div class="text-4xl mb-2">🏙️</div><div class="text-sm font-medium text-blue-800 dark:text-blue-200">' + city.region + '</div></div>';
                        }
                      }}
                    />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-4xl mb-2">🏙️</div>
                        <div className="text-sm font-medium text-blue-800 dark:text-blue-200">{city.region}</div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="pt-3 sm:pt-4">
                  <h3 className="truncate text-sm font-semibold text-foreground sm:text-base">
                    {city.name}
                  </h3>
                  <p className="truncate text-xs text-muted-foreground sm:text-sm mt-1">
                    {t('carsFrom', 'Voitures à partir de')} {(city as any).actualPrice ?? 25} {t('cars.currency', 'DH')}+
                  </p>
                </div>
              </motion.article>
            ))
          ) : (
            // Fallback to static locations
            locationsWithPrices.slice(0, 5).map((location, index) => (
              <motion.article
                key={location.id}
                initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: shouldReduceMotion ? 0 : 0.5, 
                  delay: shouldReduceMotion ? 0 : index * 0.05,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                viewport={{ once: true }}
                className="relative group cursor-pointer flex-shrink-0 w-40 sm:w-auto"
                onClick={() => handleLocationClick(location)}
              >
                <div className="relative aspect-square overflow-hidden rounded-xl">
                  <img
                    src={location.imageUrl}
                    alt={location.name}
                    className="w-full h-full object-cover transition-all duration-300 group-hover:scale-105 group-hover:brightness-110"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/images/cars/default-car.jpg'; // Fallback image
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                
                <div className="pt-3 sm:pt-4">
                  <h3 className="truncate text-sm font-semibold leading-tight text-foreground sm:text-base">
                    {location.name}
                  </h3>
                  <p className="truncate text-xs text-muted-foreground sm:text-sm mt-1">
                    {t('carsFrom', 'Voitures à partir de')} {location.actualPrice ?? location.startingPrice} {t('cars.currency', 'DH')}+
                  </p>
                </div>
              </motion.article>
            ))
          )}
        </div>
        
        {/* View All Button at Bottom */}
        <div className="flex justify-center mt-8 md:mt-12">
          <Button 
            variant="outline" 
            size="lg"
            className="text-sm font-medium px-8 py-3"
            onClick={() => navigate(`/${lang || 'fr'}/cars`)}
          >
            {t('viewAll', 'Voir Tout')}
          </Button>
        </div>
      </div>
    </section>
  )
}

export default PopularDestinations
